import { CALLS_DEFAULTS, type CallsSettings } from '../../types/speedToLead.js';
import { validateMessageTemplate } from './spokenMessages.js';

// The `calls` block of a club's settings (Stage 3): outbound call limits and the recording switches.
// PURE: the read boundary and the write validator, shared by the form and the API.
//
// ⚠️ DEFAULTS: recording and transcription are ON (Scott, 2026-10-22). Everything is Kyck-only: the portal's
// two writes (the staff list and the spoken messages) never touch this block.

export const CALLS_LIMITS = {
  maxConcurrentCalls: { min: 1, max: 20 },
  maxCallsPerDay: { min: 1, max: 500 },
  recordingRetentionDays: { min: 7, max: 365 },
} as const;

function int(value: unknown, fallback: number, min: number, max: number): number {
  return typeof value === 'number' && Number.isInteger(value) && value >= min && value <= max ? value : fallback;
}

/** The read boundary: a missing or garbage field resolves to its default (recording on, caps at 3 and 50). */
export function resolveCalls(raw: unknown): CallsSettings {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const L = CALLS_LIMITS;
  const w = validateMessageTemplate('outbound', r.whisperMessage);
  return {
    callAtNextOpening: r.callAtNextOpening === false ? false : CALLS_DEFAULTS.callAtNextOpening,
    maxConcurrentCalls: int(r.maxConcurrentCalls, CALLS_DEFAULTS.maxConcurrentCalls, L.maxConcurrentCalls.min, L.maxConcurrentCalls.max),
    maxCallsPerDay: int(r.maxCallsPerDay, CALLS_DEFAULTS.maxCallsPerDay, L.maxCallsPerDay.min, L.maxCallsPerDay.max),
    recordCalls: r.recordCalls === false ? false : CALLS_DEFAULTS.recordCalls,
    transcribeCalls: r.transcribeCalls === false ? false : CALLS_DEFAULTS.transcribeCalls,
    recordingRetentionDays: int(
      r.recordingRetentionDays,
      CALLS_DEFAULTS.recordingRetentionDays,
      L.recordingRetentionDays.min,
      L.recordingRetentionDays.max,
    ),
    // ⚠️ A stored template that no longer validates reads as the DEFAULT, never as markup or a link.
    whisperMessage: w.ok ? w.value : null,
  };
}

export type CallsShape = { ok: true; value: CallsSettings } | { ok: false; error: string };

/** The `calls` block of a write. Strict: no coercion. `whisperMessage` may be omitted (the service keeps the stored one). */
export function validateCallsShape(input: unknown): CallsShape {
  if (!input || typeof input !== 'object') return { ok: false, error: 'calls must be an object.' };
  const i = input as Record<string, unknown>;
  const L = CALLS_LIMITS;
  for (const key of ['callAtNextOpening', 'recordCalls', 'transcribeCalls'] as const) {
    if (typeof i[key] !== 'boolean') return { ok: false, error: `calls.${key} must be true or false.` };
  }
  const ranged: Array<[keyof typeof L, string]> = [
    ['maxConcurrentCalls', 'Max concurrent calls'],
    ['maxCallsPerDay', 'Max calls per day'],
    ['recordingRetentionDays', 'Recording retention'],
  ];
  for (const [key, label] of ranged) {
    const v = i[key];
    if (typeof v !== 'number' || !Number.isInteger(v) || v < L[key].min || v > L[key].max) {
      return { ok: false, error: `${label} must be a whole number from ${L[key].min} to ${L[key].max}.` };
    }
  }
  const w = i.whisperMessage === undefined ? undefined : validateMessageTemplate('outbound', i.whisperMessage);
  if (w && !w.ok) return w;
  return {
    ok: true,
    value: {
      callAtNextOpening: i.callAtNextOpening as boolean,
      maxConcurrentCalls: i.maxConcurrentCalls as number,
      maxCallsPerDay: i.maxCallsPerDay as number,
      recordCalls: i.recordCalls as boolean,
      transcribeCalls: i.transcribeCalls as boolean,
      recordingRetentionDays: i.recordingRetentionDays as number,
      // undefined (omitted) becomes the stored value in the service; the type here is the full shape.
      whisperMessage: (w && w.ok ? w.value : undefined) as string | null,
    },
  };
}

/**
 * What a call actually records. ⚠️ ONE function decides this for outbound AND inbound NCCOs, so "recording off"
 * removes every record / transcription setting from every NCCO. Transcription needs recording.
 */
export function recordingPlan(calls: Pick<CallsSettings, 'recordCalls' | 'transcribeCalls'>): { record: boolean; transcribe: boolean } {
  const record = calls.recordCalls === true;
  return { record, transcribe: record && calls.transcribeCalls === true };
}
